import { HashKeyTypes } from '@mode/shared/contract-common';

// Out-of-bounds hash code that will/must not
// collide with results produced by `hashFn`
export const DEFAULT_HASH_CODE = 2 ** 32;

/**
 * @description will return the stringified representation for any ToString()-able value.
 * For null | undefined values, the Unicode Character <Null> (NUL|U+0000) is returned
 */
function toString<T extends HashKeyTypes.Stringable>(value: null | undefined | T): string {
  return value == null ? '\0' : value.toString();
}

/**
 * @description Default hash function which returns an out-of-bounds value that
 * will not collide with any hashes produced via. `hashFn`. This is the default
 * when hashing on an empty set of keys.. everything is in the same group / produces
 * the same hash code
 */
function defaultKeyFn(): number {
  return DEFAULT_HASH_CODE;
}

/**
 * @description
 * similar to djb2 hash algo
 * see: http://www.cse.yorku.ca/~oz/hash.html
 */
export function hashFn(value: string): number {
  let index = value.length;
  let hash = 5381;

  while (index--) {
    const charCode = value.charCodeAt(index) | 0;
    hash = (hash << 5) - hash + charCode;
    hash |= 0;
  }

  return hash;
}

/**
 * @description HashFn factory. Creates a generic hash key function over
 * `keys` of `item` where the hashed values include their corresponding key
 */
export function keyed<T extends HashKeyTypes.Stringable | null | undefined>(keys: string[]): HashKeyTypes.HashFn<T> {
  if (keys.length !== 0) {
    const init = toString(keys.length);
    return (item: Record<string, T>): number => {
      return hashFn(
        keys.reduce((memo, key) => {
          return `${memo};${key}=${toString(item[key])}`;
        }, init)
      );
    };
  }
  return defaultKeyFn;
}

/**
 * @description HashFn factory. Creates a generic hash key function over
 * `keys` of `item` where the hashed values DO NOT include their corresponding key
 */
export function unkeyed<T extends HashKeyTypes.Stringable | null | undefined>(keys: string[]): HashKeyTypes.HashFn<T> {
  if (keys.length !== 0) {
    const init = toString(keys.length);
    return (item: Record<string, T>): number => {
      return hashFn(
        keys.reduce((memo, key) => {
          return `${memo};${toString(item[key])}`;
        }, init)
      );
    };
  }
  return defaultKeyFn;
}
